import { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../contexts/AuthContext';
import usePatientsForDoctor from '../../../hooks/usePatientsForDoctor';
import useFetchPreRegisteredUserForTeacher from '../../../hooks/useFetchPreRegisteredUserForTeacher';
import useStudentsForOneTeacher from '../../../hooks/useStudentsForOneTeacher';
import useFetchTeacherAllClassrooms from '../../../hooks/useFetchTeacherAllClassrooms';
import useFetchStudentAllClassrooms from '../../../hooks/useFetchStudentAllClassrooms';
import { fetchingTeachersForStudents } from '../../../api/teachers';
import { ACM } from '../../../constants/UserRoles';
import useFetchAllAttachmentsByUser from '../../../hooks/useFetchAllAttachmentsByUser';

export default function useAssessmentReportLogic() {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [goalType, setGoalType] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [shareWith, setShareWith] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(null);
// values: "all", "Vineland Assessment", "other"
console.log("selectedAssessmentType:", selectedAssessmentType);

  const { user, typeOfUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch data hooks
  const { data: fetchStudentsForOneTeacher = [] } = useStudentsForOneTeacher(
    user?.uid,
    typeOfUser
  );

  const enrolledStudents = useMemo(
    () => fetchStudentsForOneTeacher || [],
    [fetchStudentsForOneTeacher]
  );

  const { data: preRegisteredUsers = [] } =
    useFetchPreRegisteredUserForTeacher(user?.uid);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const studentsList = [...enrolledStudents, ...preRegisteredUsers];
  const { data: patientData = [] } = usePatientsForDoctor(user?.uid);
  
  // Fetch classrooms for teachers
  const { data: teacherClassrooms = [] } = useFetchTeacherAllClassrooms(
    ACM[typeOfUser]?.isTeacher ? user?.uid : null
  );

  // Fetch classrooms for students
  const { data: studentClassrooms = [] } = useFetchStudentAllClassrooms(
    ACM[typeOfUser]?.isStudent ? user?.uid : null
  );

  // Use appropriate classrooms based on user type
  const classrooms = ACM[typeOfUser]?.isStudent ? studentClassrooms : teacherClassrooms;


const { data: allAttachments, isLoading: isLoadingGoalData } = useFetchAllAttachmentsByUser(
  user?.uid,
  typeOfUser
);

  // Process students goal data
  const studentGoalsData = useMemo(() => {
    if (!allAttachments?.data?.length || !user?.uid) return [];
  
    const isStudent = ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient;
    
    // For students/patients: data is a flat array of attachments
    if (isStudent) {
      const validAttachments = allAttachments.data
        .filter(attachment => {
          const docData = attachment?.docData || attachment;
          if (!docData) return false;
          
          // For students, all attachments returned are for them
          // Just filter out deleted ones
          const isNotDeleted = !docData.isDeleted || 
            (Array.isArray(docData.isDeleted) && !docData.isDeleted.includes('Parent/Guardian'));
          
          return isNotDeleted;
        })
        .map(attachment => ({
          docId: attachment.docId || attachment.id,
          docData: attachment.docData || attachment,
        }));
      
      // Group by patientId (which is user.uid for students)
      if (validAttachments.length > 0) {
        return [{
          patientId: user.uid,
          attachmentData: validAttachments,
        }];
      }
      return [];
    }
    
    // For teachers/doctors: data is grouped by patientId
    return allAttachments.data.filter(patient => {
      // If a patient has no attachments, there's nothing to check, so no access.
      if (!patient.attachmentData || patient.attachmentData.length === 0) {
        return false;
      }
  
      // Use .some() to check if the user has access to AT LEAST ONE attachment.
      // .some() is efficient because it stops as soon as it finds a match.
      return patient.attachmentData.some(attachment => {
        const docData = attachment.docData;
        if (!docData) return false;
  
        // Rule 1: The user who uploaded the document always has access.
        if (docData.UploadedBy === user.uid) {
          return true;
        }
  
        // Rule 2: Check attachment-level sharing (for assessments, etc.).
        if (Array.isArray(docData.shareWithTeacherIds)) {
          if (docData.shareWithTeacherIds.includes(user.uid) || docData.shareWithTeacherIds.includes('Parent/Guardian')) {
            return true;
          }
        }
  
        // Rule 3: Check goal-level sharing inside a goal_summary.
        if (Array.isArray(docData.goal_summary) && docData.goal_summary.length > 0) {
          // Check if AT LEAST ONE goal is shared with the user.
          const hasAccessToAnyGoal = docData.goal_summary.some(goal => {
            if (!goal.ShareWithTeam) {
              return goal.GoalDetails?.GoalAuthor === user.uid;
            }
            const { EditAccess = [], ViewOnlyAccess = [] } = goal.ShareWithTeam;
            // Combine the arrays and check for the user's ID or role.
            return [...EditAccess, ...ViewOnlyAccess].includes(user.uid) || [...EditAccess, ...ViewOnlyAccess].includes('Parent/Guardian');
          });
  
          if (hasAccessToAnyGoal) {
            return true;
          }
        }
  
        // If none of the above rules grant access, this attachment doesn't count.
        return false;
      }); // End of patient.attachmentData.some()
    }); // End of allAttachments.data.filter()
  
  }, [allAttachments, user?.uid, typeOfUser]);
  
  console.log('studentGoalsData', studentGoalsData);


  // Filter goals based on selections
  const filteredStudentGoals = useMemo(() => {
    if (!studentGoalsData || studentGoalsData.length === 0) return [];
  
    return studentGoalsData
      .filter((student) => {
        // 1️⃣ FILTER BY SELECTED STUDENT/PATIENT
        // For students/patients, always filter to their own data
        if (ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) {
          return student.patientId === user?.uid;
        }
        
        if (!selectedStudent && !selectedPatient) return true;
  
        // Teacher selects student
        if (selectedStudent) {
          return student.patientId === selectedStudent;
        }
  
        // Doctor selects patient
        if (selectedPatient) {
          return student.patientId === selectedPatient;
        }
  
        return true;
      })
  
      .map((student) => {
        const attachments = student.attachmentData || [];
  
        // 2️⃣ ⭐ APPLY DYNAMIC ASSESSMENT FILTER HERE ⭐
        let filteredAttachments = attachments;
  
        if (selectedAssessmentType) {
          filteredAttachments = attachments.filter((a) => {
            const type = (a.docData?.typeOfAssessment || "").trim().toLowerCase();
            const selected = (selectedAssessmentType || "").trim().toLowerCase();
            return type === selected;
          });
        }
        
  
        return {
          ...student,
          attachmentData: filteredAttachments,
        };
      })
  
      // 3️⃣ REMOVE STUDENTS WHO HAVE NO MATCHING ATTACHMENTS
      .filter((student) => student.attachmentData.length > 0);
  
  }, [studentGoalsData, selectedStudent, selectedPatient, selectedAssessmentType, typeOfUser, user?.uid]);
  
  

  console.log(filteredStudentGoals,'filteredStudentGoals');
  // Effects
  useEffect(() => {
    setShareWith([]);
    const fetchTeachers = async () => {
      // For students, fetch their own teachers using their uid
      // For teachers, fetch teachers for the selected student
      const studentId = ACM[typeOfUser]?.isStudent ? user?.uid : selectedStudent;
      
      if (studentId) {
        const result = await fetchingTeachersForStudents(studentId);
        const filteredData = result?.filter((item) => item.uid !== user?.uid);
        setTeachers(filteredData || []);
        
        // For students, automatically add all teachers to shareWith
        if (ACM[typeOfUser]?.isStudent && filteredData?.length > 0) {
          const teacherIds = filteredData.map((teacher) => teacher.uid);
          setShareWith(teacherIds);
        }
      } else {
        setTeachers([]);
      }
    };

    fetchTeachers();
  }, [selectedStudent, user?.uid, typeOfUser]);

  // Auto-select student for teachers, and set user.uid for students/patients
  useEffect(() => {
    if (ACM[typeOfUser]?.isTeacher && studentsList.length > 0 && !selectedStudent) {
      setSelectedStudent(studentsList[0]?.id || '');
    } else if ((ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient) && !selectedStudent) {
      // For students/patients, use their own ID
      setSelectedStudent(user?.uid || '');
    }
  }, [studentsList, selectedStudent, typeOfUser, user?.uid]);

  useEffect(() => {
    if (patientData.length > 0 && !selectedPatient) {
      setSelectedPatient(patientData[0]?.patientId || '');
    }
  }, [patientData, selectedPatient]);

  return {
    // State
    selectedStudent,
    selectedPatient,
    goalType,
    selectedAssessmentType,
    dateRange,
    shareWith,
    selectedGoals,
    confirmDialogOpen,
    pendingAction,
    teachers,
    isGenerating,
    selectedClassrooms,
    
    // Data
    studentsList,
    patientData,
    preRegisteredUsers,
    filteredStudentGoals,
    studentGoalsData,
    isLoadingGoalData,
    user,
    typeOfUser,
    classrooms,
    
    // Setters
    setSelectedStudent,
    setSelectedPatient,
    setGoalType,
    setSelectedAssessmentType,
    setDateRange,
    setShareWith,
    setSelectedGoals,
    setConfirmDialogOpen,
    setPendingAction,
    setIsGenerating,
    setSelectedClassrooms,
    
    // Navigation
    navigate,
  };
} 